import { FC } from 'react'

import { TypedDocumentNode } from '@apollo/client'
import {
  ProcurementPurchaseOrderFilter,
  PurchaseOrdersOrderedWidgetDocument,
  PurchaseOrdersOrderedWidgetQuery,
  PurchaseOrdersPendingWidgetDocument,
  PurchaseOrdersPendingWidgetQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface QueryArgsType {
  currency: string
  companyId: number
  purchaseOrderFilters?: ProcurementPurchaseOrderFilter
  search?: string
  vendorId?: number
}

interface PreparedDataArgsType {
  amountFormatter: (value: number) => string
}

type WidgetConfig<TQuery> = {
  id: string
  document: TypedDocumentNode<TQuery, QueryArgsType>
  prepareData: (
    data: TQuery,
    prepareDataArgs: PreparedDataArgsType
  ) => { groupedChartData: ChartData[] }
}

const getLabelByStatus = (status: string) => {
  switch (status) {
    case 'ordered':
      return i18n.t('procurement.purchase_orders.status.ordered')
    case 'pending':
      return i18n.t('procurement.purchase_orders.status.pending')
    default:
      return status
  }
}

const createPrepareData =
  (status: string) =>
  (
    data: PurchaseOrdersOrderedWidgetQuery | PurchaseOrdersPendingWidgetQuery,
    prepareDataArgs: PreparedDataArgsType
  ) => {
    const amount = data?.procurement?.purchaseOrdersConnection?.aggregate?.sum?.amount || 0
    const count = data?.procurement?.purchaseOrdersConnection?.aggregate?.count || 0

    const groupedChartData: ChartData[] = [
      {
        status,
        label: `${getLabelByStatus(status)} (${count})`,
        value: amount,
        formattedValue: prepareDataArgs.amountFormatter(amount),
      },
    ]

    return { groupedChartData }
  }

const createWidgetBuilder = <TQuery,>({ id, document, prepareData }: WidgetConfig<TQuery>) => {
  return ({
    queryArgs,
    prepareDataArgs,
    filters,
  }: {
    queryArgs: QueryArgsType
    prepareDataArgs: PreparedDataArgsType
    filters: ProcurementPurchaseOrderFilter
  }) => {
    const WidgetComponent: FC<{ data: ReturnType<typeof prepareData> }> = ({ data }) => {
      const { groupedChartData } = data

      return (
        <FinanceWidget
          id={id}
          type={SupportedCharts.COUNTERS}
          showChart={false}
          countersChartDatas={groupedChartData}
          chartDatas={groupedChartData}
        />
      )
    }

    const filteredPurchaseOrdersFilters = removeNullBetween(filters)

    return buildWidget({
      id,
      component: WidgetComponent,
      document,
      prepareData: (data: TQuery) => prepareData(data, prepareDataArgs),
      queryArgs: {
        ...queryArgs,
        purchaseOrderFilters: { ...filteredPurchaseOrdersFilters },
      },
    })
  }
}

export const buildPurchaseOrdersPendingAmountInsightWidget = createWidgetBuilder({
  id: 'purchaseOrdersClosedWidget',
  document: PurchaseOrdersPendingWidgetDocument,
  prepareData: createPrepareData('pending'),
})

export const buildPurchaseOrdersOrderedAmountInsightWidget = createWidgetBuilder({
  id: 'purchaseOrdersTotalWidget',
  document: PurchaseOrdersOrderedWidgetDocument,
  prepareData: createPrepareData('ordered'),
})
